import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [debug, setDebug] = useState('');
  const [tokenDebug, setTokenDebug] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setDebug('Form submitted with: ' + JSON.stringify(formData));
    setLoading(true);
    setError('');

    try {
      setDebug(prev => prev + '\nCalling login function...');
      
      // Capture any console.log messages by overriding console.log temporarily
      const originalLog = console.log;
      const logs = [];
      console.log = (...args) => {
        logs.push(args.join(' '));
        originalLog.apply(console, args);
      };
      
      const result = await login(formData.email, formData.password);
      
      // Restore console.log
      console.log = originalLog;
      
      setDebug(prev => prev + '\nConsole logs: ' + logs.join('\n'));
      setDebug(prev => prev + '\nLogin result: ' + JSON.stringify(result));
      
      // Check token after login
      const storedToken = localStorage.getItem('token');
      setTokenDebug('Token in localStorage: ' + (storedToken ? storedToken.substring(0, 20) + '...' : 'NOT FOUND'));
      
      if (result.success) {
        setDebug(prev => prev + '\nLogin successful, navigating...');
        navigate('/');
      } else {
        setDebug(prev => prev + '\nLogin failed: ' + result.error);
        setError(result.error);
      }
    } catch (error) {
      setDebug(prev => prev + '\nError caught: ' + error.message);
      setError('An unexpected error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box
            sx={{
              backgroundColor: 'primary.main',
              borderRadius: '50%',
              width: 56,
              height: 56,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 2,
            }}
          >
            <LockOutlined sx={{ color: 'white' }} />
          </Box>
          
          <Typography component="h1" variant="h5" gutterBottom>
            Sign In
          </Typography>
          
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Lead Data Application
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {debug && (
            <Alert severity="info" sx={{ width: '100%', mb: 2 }}>
              <pre style={{ fontSize: '12px', margin: 0 }}>{debug}</pre>
            </Alert>
          )}
          
          {tokenDebug && (
            <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>
              <pre style={{ fontSize: '12px', margin: 0 }}>{tokenDebug}</pre>
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Sign In'}
            </Button>
            
            <Button
              type="button"
              fullWidth
              variant="outlined"
              sx={{ mb: 2 }}
              onClick={() => {
                const token = localStorage.getItem('token');
                setTokenDebug('Token in localStorage: ' + (token ? token.substring(0, 50) + '...' : 'NOT FOUND'));
                setDebug('Token check completed at: ' + new Date().toLocaleTimeString());
              }}
            >
              Check Token Status
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Link to="/register" style={{ textDecoration: 'none' }}>
                <Typography variant="body2" color="primary">
                  Don't have an account? Sign Up
                </Typography>
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login; 